import { configureStore, isRejectedWithValue, Middleware, MiddlewareAPI } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';

import authReducer from './slice/authSlice';
import dashboardReducer from './slice/dashboardSlice';

// These are kept here during migration. Each one will be deleted once all
// consumers of its corresponding action file are migrated to RTK Query hooks.
import beneficiariesReducer from './slice/beneficiariesSlice';
import paymentsReducer from './slice/paymentsSlice';
import custodianReducer from './slice/custodianSlice';
import employeesReducer from './slice/employeesSlice';
import salaryPaymentReducer from './slice/salaryPaymentSlice';
import employersReducer from '../features/employer/employerSlice';

// Import new API modules here as each phase completes.
import { authApi } from '../features/auth/authApi';
import { usersApi } from '../features/shared/usersApi';
import { workflowApi } from '../features/shared/workflowApi';
import { productsApi } from '../features/shared/productsApi';
import { employerApi } from '../features/employer/employerApi';
import { paymentsApi } from '../features/pension/paymentsApi';
import { beneficiariesApi } from '../features/pension/beneficiariesApi';
import { custodianApi } from '../features/pension/custodianApi';
import { notificationsApi } from '../features/pension/notificationsApi';
import { walletApi } from '../features/pension/walletApi';
import { approvalApi } from '../features/pension/approvalApi';

import { employeesApi } from '../features/salary/employeesApi';
import { salaryPaymentsApi } from '../features/salary/salaryPaymentsApi';
import { salaryWalletApi } from '../features/salary/salaryWalletApi';

// Fires toast.error() automatically for any rejected query or mutation.
// Components that want to handle errors themselves should use:
//   const [mutate] = useSomeMutation();
//   try { await mutate(data).unwrap(); } catch (err) { /* custom handling */ }
// When using .unwrap(), the rejection does NOT reach this middleware.

const rtkQueryErrorToastMiddleware: Middleware =
  (api: MiddlewareAPI) => (next) => (action) => {
    // Check if this is an RTK Query rejected action
    if (isRejectedWithValue(action)) {
      // Look for the suppressGlobalErrorToast flag
      const meta = action.meta as any;
      if (meta && meta.arg && meta.arg.originalArgs?.suppressGlobalErrorToast) {
        return next(action);
      }
      
      const payload = action.payload as any;
      let errorMessage = 'An error occurred';
      
      if (payload?.data?.message) {
        errorMessage = payload.data.message;
      } else if (payload?.message) {
        errorMessage = payload.message;
      } else if (payload?.error) {
        errorMessage = payload.error;
      } else if (typeof payload === 'string') {
        errorMessage = payload;
      }
      
      toast.error(errorMessage);
    }

    return next(action);
  };


export const store = configureStore({
  reducer: {
    // RTK Query APIs
    [authApi.reducerPath]: authApi.reducer,
    [usersApi.reducerPath]: usersApi.reducer,
    [workflowApi.reducerPath]: workflowApi.reducer,
    [productsApi.reducerPath]: productsApi.reducer,
    [employerApi.reducerPath]: employerApi.reducer,
    [paymentsApi.reducerPath]: paymentsApi.reducer,
    [beneficiariesApi.reducerPath]: beneficiariesApi.reducer,
    [custodianApi.reducerPath]: custodianApi.reducer,
    [notificationsApi.reducerPath]: notificationsApi.reducer,
    [walletApi.reducerPath]: walletApi.reducer,
    [approvalApi.reducerPath]: approvalApi.reducer,
    [employeesApi.reducerPath]: employeesApi.reducer,
    [salaryPaymentsApi.reducerPath]: salaryPaymentsApi.reducer,
    [salaryWalletApi.reducerPath]: salaryWalletApi.reducer,

    // Client state
    auth: authReducer,
    dashboard: dashboardReducer,

    // LEGACY server-state slices (removed phase by phase during migration)
    beneficiaries: beneficiariesReducer,
    payments: paymentsReducer,
    custodians: custodianReducer,
    employees: employeesReducer,
    employers: employersReducer,
    salaryPayment: salaryPaymentReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .concat(authApi.middleware)
      .concat(usersApi.middleware)
      .concat(workflowApi.middleware)
      .concat(productsApi.middleware)
      .concat(employerApi.middleware)
      .concat(paymentsApi.middleware)
      .concat(beneficiariesApi.middleware)
      .concat(custodianApi.middleware)
      .concat(notificationsApi.middleware)
      .concat(walletApi.middleware)
      .concat(approvalApi.middleware)
      .concat(employeesApi.middleware)
      .concat(salaryPaymentsApi.middleware)
      .concat(salaryWalletApi.middleware)
      .concat(rtkQueryErrorToastMiddleware),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

/** Direct store dispatch for use outside React components (legacy usage during migration).
 *  Prefer useAppDispatch() inside components. */
export const appDispatch = store.dispatch;
